import { Component, OnDestroy, OnInit } from "@angular/core";
import { nativeAppUrl } from "../../shared/utils/native-app-url.utils";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, fromEvent, merge, of, timer } from "rxjs";
import { catchError, debounceTime, filter, finalize, map, switchMap, take } from "rxjs/operators";
import { AuthRedirectService } from "../../shared/services/auth-redirect.service";
import {
  GuestUpgradeClaimService,
  GuestUpgradeTransaction
} from "../../shared/services/guest-upgrade-claim.service";
import { PlayerService } from "../../shared/services/player.service";
import { AnalyticsService } from "../../shared/services/analytics.service";
import { AuthenticatedSessionStateService } from "../../shared/services/authenticated-session-state.service";

type GuestUpgradeViewState = "PREPARING" | "READY" | "AUTHENTICATING" | "COMPLETING" | "HANDED_OFF" | "COMPLETED" | "ERROR";

@Component({
  selector: "app-guest-upgrade",
  templateUrl: "./guest-upgrade.component.html",
  styleUrls: ["./guest-upgrade.component.css"],
  standalone: false
})
export class GuestUpgradeComponent implements OnInit, OnDestroy {
  private static readonly HANDOFF_STATUS_CHECK_INTERVAL_MS = 1000;
  private static readonly HANDOFF_STATUS_CHECK_COUNT = 12;
  public state: GuestUpgradeViewState = "PREPARING";
  public transaction: GuestUpgradeTransaction;
  public errorMessage = "";
  public errorMessageKey = "";
  public handoffUrl = "";
  public handoffLoading = false;
  public isMobileBrowser = false;
  public reauthenticationRequired = false;
  private subscriptions = new Subscription();
  private handoffStatusChecks: Subscription;
  private handoffStatusRequestInFlight = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private authRedirect: AuthRedirectService,
    private upgradeService: GuestUpgradeClaimService,
    private playerService: PlayerService,
    private translate: TranslateService,
    private analytics: AnalyticsService,
    private authenticatedSessionState: AuthenticatedSessionStateService
  ) {}

  public ngOnInit(): void {
    this.isMobileBrowser = this.detectMobileBrowser();
    this.observeBrowserReturn();
    const handoffCode = this.route.snapshot.queryParamMap.get("handoff");
    const pendingToken = this.upgradeService.pendingUpgradeToken();
    const prepare$ = handoffCode
      ? this.upgradeService.redeemHandoff(handoffCode)
      : pendingToken
        ? this.upgradeService.pendingUpgradeStatus()
        : this.prepareFromRoute().pipe(
          map((transaction) => transaction || this.upgradeService.recentCompletedUpgrade())
        );

    this.subscriptions.add(prepare$.pipe(
      catchError((error) => {
        this.showError(error);
        return of(null);
      })
    ).subscribe((transaction) => {
      if (!transaction) {
        if (this.state !== "ERROR") {
          this.showError();
        }
        return;
      }
      if (handoffCode) {
        // The service has persisted the replacement token. Never replay the
        // consumed one-time code on refresh or when returning through history.
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { handoff: null },
          queryParamsHandling: "merge",
          preserveFragment: true,
          replaceUrl: true
        });
      }
      if (this.upgradeService.wasRevokedByAccountDeletion(transaction)) {
        this.upgradeService.clearAllTransitionState();
        this.router.navigate(["/"]);
        return;
      }
      this.transaction = transaction;
      this.analytics.track("guest_upgrade_started", {
        screen: "guest_upgrade",
        entityType: "event",
        entityId: transaction.eventId,
        properties: { type: transaction.type, platform: "web" }
      });
      if (transaction.status === "COMPLETED") {
        this.state = "COMPLETED";
        return;
      }
      if (transaction.readOnlyHandoff) {
        this.state = "HANDED_OFF";
        return;
      }
      this.state = "READY";
      this.continueIfAuthenticated();
    }));
  }

  public ngOnDestroy(): void {
    this.stopHandoffStatusChecks();
    this.subscriptions.unsubscribe();
  }

  public createAccount(): void {
    this.authenticate(true);
  }

  public login(): void {
    this.authenticate(false);
  }

  private authenticate(signup: boolean): void {
    if (this.state !== "READY") {
      return;
    }
    this.state = "AUTHENTICATING";
    this.analytics.track("guest_upgrade_auth_started", {
      screen: "guest_upgrade",
      entityType: "event",
      entityId: this.transaction && this.transaction.eventId,
      properties: { type: this.transaction && this.transaction.type, platform: "web" }
    });
    (signup ? this.authRedirect.signup("/guest-upgrade") : this.authRedirect.login("/guest-upgrade")).subscribe({
      error: (error) => this.showError(error)
    });
  }

  public retry(): void {
    if (this.state === "HANDED_OFF") {
      this.refreshHandoffStatus();
      return;
    }
    if (!this.transaction && this.route.snapshot.queryParamMap.get("mode") === "public-invite") {
      this.returnToGuest();
      return;
    }
    if (this.reauthenticationRequired || this.isUnstartedParticipantUpgrade()) {
      this.restartAccountCreation();
      return;
    }
    this.state = "COMPLETING";
    this.complete();
  }

  public returnToGuest(): void {
    const inviteToken = this.route.snapshot.queryParamMap.get("inviteToken");
    if (inviteToken || this.transaction && this.transaction.type === "PUBLIC_INVITE_PARTICIPANT") {
      this.router.navigate(inviteToken ? ["/join", inviteToken] : ["/"]);
      return;
    }
    this.router.navigate(["/guest-manager"]);
  }

  public openMatch(): void {
    if (!this.transaction || !this.transaction.eventId) {
      this.router.navigate(["/event-list"]);
      return;
    }
    if (this.transaction.readOnlyHandoff) {
      if (!this.isMobileBrowser) {
        this.returnToGuest();
        return;
      }
      this.upgradeService.clearCompletedUpgradeState(this.transaction);
      this.upgradeService.claimedMatchAppUrl(this.transaction.type, this.transaction.eventId, this.transaction)
        .subscribe({ next: (url) => window.location.href = url, error: (error) => this.showError(error) });
      return;
    }
    this.router.navigate(["/event-players", false, { eventId: this.transaction.eventId }]);
  }

  public openMatchLabelKey(): string {
    if (this.transaction && this.transaction.readOnlyHandoff && !this.isMobileBrowser) {
      return "guestUpgrade.flow.returnToGuest";
    }
    return this.transaction && this.transaction.readOnlyHandoff
      ? "guestUpgrade.flow.openMatchInApp"
      : "guestUpgrade.flow.openMatch";
  }

  public continueInApp(): void {
    if (!this.isMobileBrowser || this.handoffLoading || !this.transaction) {
      return;
    }
    this.handoffLoading = true;
    this.upgradeService.issueHandoff().subscribe({
      next: (handoff) => {
        this.handoffLoading = false;
        if (!handoff) {
          return;
        }
        this.handoffUrl = nativeAppUrl(`guest-upgrade?handoff=${encodeURIComponent(handoff.handoffCode)}`);
        this.state = "HANDED_OFF";
        this.startHandoffStatusChecks();
        window.location.href = this.handoffUrl;
      },
      error: (error) => {
        this.handoffLoading = false;
        this.showError(error);
      }
    });
  }

  public eventName(): string {
    return this.transaction && this.transaction.eventName
      ? this.transaction.eventName
      : this.translate.instant("guestUpgrade.flow.yourMatch");
  }

  public isParticipantUpgrade(): boolean {
    return this.transaction && this.transaction.type === "PUBLIC_INVITE_PARTICIPANT";
  }

  public retryLabelKey(): string {
    if (this.state === "HANDED_OFF") {
      return "guestUpgrade.flow.checkAppStatus";
    }
    if (!this.transaction && this.route.snapshot.queryParamMap.get("mode") === "public-invite") {
      return "guestUpgrade.flow.returnToGuest";
    }
    return this.reauthenticationRequired || this.isUnstartedParticipantUpgrade()
      ? "guestUpgrade.flow.continueAccountCreation"
      : "common.tryAgain";
  }

  private prepareFromRoute() {
    const mode = this.route.snapshot.queryParamMap.get("mode");
    if (mode === "guest-owner") {
      return this.upgradeService.prepareGuestOwnerUpgrade(this.route.snapshot.queryParamMap.get("eventId"));
    }
    if (mode === "public-invite") {
      return this.upgradeService.preparePublicInviteUpgrade(this.route.snapshot.queryParamMap.get("inviteToken"));
    }
    return of(null);
  }

  private continueIfAuthenticated(): void {
    this.subscriptions.add(this.auth.isLoading$.pipe(
      filter((loading) => !loading),
      take(1),
      switchMap(() => this.auth.isAuthenticated$.pipe(take(1))),
      switchMap((authenticated) => authenticated
        ? this.playerService.currentPlayer$.pipe(filter((player) => !!player), take(1))
        : of(null))
    ).subscribe((player) => {
      if (player) {
        this.state = "COMPLETING";
        this.complete();
      }
    }));
  }

  private refreshHandoffStatus(showLoading: boolean = true): void {
    if (this.handoffStatusRequestInFlight || this.state === "COMPLETED") {
      return;
    }
    this.handoffStatusRequestInFlight = true;
    if (showLoading) {
      this.state = "PREPARING";
    }
    this.subscriptions.add(this.upgradeService.pendingUpgradeStatus().pipe(
      finalize(() => this.handoffStatusRequestInFlight = false)
    ).subscribe({
      next: (transaction) => {
        if (!transaction) {
          if (showLoading) {
            this.showError();
          }
          return;
        }
        this.transaction = transaction;
        if (this.upgradeService.wasRevokedByAccountDeletion(transaction)) {
          this.upgradeService.clearAllTransitionState();
          this.router.navigate(["/"]);
          return;
        }
        this.state = transaction.status === "COMPLETED" ? "COMPLETED" : "HANDED_OFF";
        if (this.state === "COMPLETED") {
          this.stopHandoffStatusChecks();
        }
      },
      error: (error) => {
        if (showLoading) {
          this.showError(error);
        }
      }
    }));
  }

  private observeBrowserReturn(): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }
    this.subscriptions.add(merge(
      fromEvent(window, "focus"),
      fromEvent(window, "pageshow"),
      fromEvent(document, "visibilitychange")
    ).pipe(
      filter(() => document.visibilityState !== "hidden"),
      debounceTime(100)
    ).subscribe(() => {
      if (this.state === "HANDED_OFF") {
        this.startHandoffStatusChecks();
      }
    }));
  }

  private startHandoffStatusChecks(): void {
    if (this.state !== "HANDED_OFF") {
      return;
    }
    this.stopHandoffStatusChecks();
    this.handoffStatusChecks = timer(0, GuestUpgradeComponent.HANDOFF_STATUS_CHECK_INTERVAL_MS).pipe(
      take(GuestUpgradeComponent.HANDOFF_STATUS_CHECK_COUNT)
    ).subscribe(() => this.refreshHandoffStatus(false));
  }

  private stopHandoffStatusChecks(): void {
    if (this.handoffStatusChecks) {
      this.handoffStatusChecks.unsubscribe();
      this.handoffStatusChecks = null;
    }
  }

  private complete(): void {
    this.upgradeService.claimPendingGuestState(false).subscribe({
      next: (result) => {
        if (!result || !result.success) {
          this.reauthenticationRequired = this.requiresFreshAuthentication(result);
          const resultMessage = result && result.message || "";
          this.errorMessageKey = resultMessage.indexOf("guestUpgrade.") === 0
            ? resultMessage
            : resultMessage ? "" : "guestUpgrade.errors.moveGuestStateFailed";
          this.errorMessage = this.errorMessageKey ? "" : resultMessage;
          this.state = "ERROR";
          return;
        }
        this.transaction = Object.assign({}, this.transaction, {
          status: "COMPLETED",
          eventId: result.eventId || this.transaction.eventId
        });
        this.analytics.track("guest_upgrade_completed", {
          screen: "guest_upgrade",
          entityType: "event",
          entityId: this.transaction.eventId,
          properties: { type: this.transaction.type, platform: "web" }
        });
        this.state = "COMPLETED";
      },
      error: (error) => this.showError(error)
    });
  }

  private showError(error?: any): void {
    this.reauthenticationRequired = false;
    const backendMessage = error && error.error && error.error.message ? error.error.message : "";
    this.errorMessageKey = backendMessage.indexOf("guestUpgrade.") === 0
      ? backendMessage
      : backendMessage ? "" : "guestUpgrade.errors.recoveryRequired";
    this.errorMessage = this.errorMessageKey ? "" : backendMessage;
    this.state = "ERROR";
  }

  private requiresFreshAuthentication(result: any): boolean {
    const status = result && result.error && result.error.status;
    return (status === 401 || status === 403)
      && !!this.transaction
      && this.transaction.attemptCount === 0;
  }

  private restartAccountCreation(): void {
    this.reauthenticationRequired = false;
    this.state = "AUTHENTICATING";
    this.authenticatedSessionState.clear();
    this.playerService.clearCurrentPlayer();
    this.authRedirect.signup("/guest-upgrade").subscribe({
      error: (error) => this.showError(error)
    });
  }

  private isUnstartedParticipantUpgrade(): boolean {
    return !!this.transaction
      && this.transaction.type === "PUBLIC_INVITE_PARTICIPANT"
      && this.transaction.attemptCount === 0;
  }

  private detectMobileBrowser(): boolean {
    if (typeof navigator === "undefined") {
      return false;
    }

    const userAgent = navigator.userAgent || "";
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const iPadDesktopMode = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    return mobileUserAgent || iPadDesktopMode;
  }
}
